/**
 * Support tickets + support-plan billing + live support calls — React Query
 * hooks and API helpers (customer + agent surfaces).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiPaths } from "./api";
import { tokenStore } from "./tokenStore";
import type { Paginated } from "./types";

// These lists are now paginated server-side; request a bounded page that
// preserves the agent queue's prior 200-row cap. Page controls can be
// added later if a desk routinely exceeds this.
const SUPPORT_PAGE_SIZE = "200";

export type SupportPlan = {
  slug: string;
  name: string;
  description: string;
  monthly_price_eur: string;
  hourly_price_eur: string;
  first_response_sla_hours: number;
  allows_live_call: boolean;
  is_self_serve: boolean;
  sort_order: number;
};

export type SupportSubscription = {
  plan: SupportPlan;
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
};

export type TicketStatus =
  | "open"
  | "pending_agent"
  | "pending_customer"
  | "resolved"
  | "closed";

export type Ticket = {
  id: string;
  subject: string;
  status: TicketStatus;
  priority: string;
  category: string;
  support_plan_slug: string;
  first_response_due_at: string | null;
  first_responded_at: string | null;
  resolved_at: string | null;
  closed_at: string | null;
  requester_email: string | null;
  requester_name: string | null;
  assignee_email: string | null;
  live_call_session: string | null;
  message_count?: number;
  created_at: string;
  updated_at: string;
  org_id?: number;
  org_name?: string;
};

export type TicketMessage = {
  id: string;
  kind: "text" | "system";
  body: string;
  payload_jsonb: Record<string, unknown>;
  author_is_agent: boolean;
  author_display: string;
  is_internal_note: boolean;
  attachments: { id: string; filename: string; mime: string }[];
  created_at: string;
};

export type TicketDetail = { ticket: Ticket; messages: TicketMessage[] };

export type CallPayload = {
  session_id: string;
  room: string;
  token: string;
  url: string;
};

const enabled = () => !!tokenStore.getAccess();

/* ----------------------------- customer ----------------------------- */
export function useSupportPlans() {
  return useQuery<SupportPlan[]>({
    queryKey: ["support-plans"],
    queryFn: async () => (await api.get<SupportPlan[]>(ApiPaths.supportPlans)).data,
    enabled: enabled(),
  });
}

export function useSupportSubscription() {
  return useQuery<SupportSubscription>({
    queryKey: ["support-subscription"],
    queryFn: async () =>
      (await api.get<SupportSubscription>(ApiPaths.supportSubscription)).data,
    enabled: enabled(),
  });
}

export function useSelectSupportPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (plan: string) =>
      (await api.post<{ checkout_url?: string }>(
        ApiPaths.supportSubscriptionSelect,
        { plan },
      )).data,
    onSuccess: (data) => {
      // Paid (Pro) upgrade → Stripe Checkout: redirect the browser.
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      void qc.invalidateQueries({ queryKey: ["support-subscription"] });
    },
  });
}

/** Open the Stripe customer portal to view/download support invoices. */
export async function openSupportPortal(): Promise<void> {
  const { data } = await api.post<{ portal_url?: string }>(
    ApiPaths.supportSubscriptionPortal,
  );
  if (data?.portal_url) window.location.href = data.portal_url;
}

export function useTickets(status?: string) {
  return useQuery<Ticket[]>({
    queryKey: ["support-tickets", status ?? "all"],
    queryFn: async () =>
      (
        await api.get<Paginated<Ticket>>(ApiPaths.supportTickets, {
          params: status
            ? { status, page_size: SUPPORT_PAGE_SIZE }
            : { page_size: SUPPORT_PAGE_SIZE },
        })
      ).data.results ?? [],
    enabled: enabled(),
  });
}

export function useTicket(id: string) {
  return useQuery<TicketDetail>({
    queryKey: ["support-ticket", id],
    queryFn: async () => (await api.get<TicketDetail>(ApiPaths.supportTicket(id))).data,
    enabled: enabled() && !!id,
    refetchInterval: 15_000,
  });
}

export function useCreateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      subject: string;
      body: string;
      priority: string;
      category: string;
    }) => (await api.post<Ticket>(ApiPaths.supportTickets, input)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["support-tickets"] }),
  });
}

export function useReplyTicket(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: string) =>
      (await api.post(ApiPaths.supportTicketReply(id), { body })).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["support-ticket", id] }),
  });
}

export async function startSupportCall(ticketId: string): Promise<CallPayload> {
  return (await api.post<CallPayload>(ApiPaths.supportTicketCall(ticketId))).data;
}

export async function endSupportCall(sessionId: string): Promise<void> {
  await api.post(ApiPaths.supportCallEnd(sessionId));
}

/* ------------------------------ agent ------------------------------- */
export type AgentMe = {
  is_support_staff: boolean;
  is_platform_staff: boolean;
  mfa_enabled: boolean;
  email: string;
};

export function useAgentMe() {
  return useQuery<AgentMe>({
    queryKey: ["support-agent-me"],
    queryFn: async () => (await api.get<AgentMe>(ApiPaths.supportAgentMe)).data,
    enabled: enabled(),
    staleTime: 60_000,
    retry: false,
  });
}

export type AgentStats = {
  open: number;
  waiting_customer: number;
  unassigned: number;
  mine: number;
  overdue: number;
  resolved: number;
};

export function useAgentStats() {
  return useQuery<AgentStats>({
    queryKey: ["support-agent-stats"],
    queryFn: async () =>
      (await api.get<AgentStats>(ApiPaths.supportAgentStats)).data,
    enabled: enabled(),
    refetchInterval: 20_000,
  });
}

export function useAgentQueue(params: Record<string, string>) {
  return useQuery<Ticket[]>({
    queryKey: ["support-agent-queue", params],
    queryFn: async () =>
      (
        await api.get<Paginated<Ticket>>(ApiPaths.supportAgentTickets, {
          params: { page_size: SUPPORT_PAGE_SIZE, ...params },
        })
      ).data.results ?? [],
    enabled: enabled(),
    refetchInterval: 20_000,
  });
}

export function useAgentTicket(id: string) {
  return useQuery<TicketDetail>({
    queryKey: ["support-agent-ticket", id],
    queryFn: async () =>
      (await api.get<TicketDetail>(ApiPaths.supportAgentTicket(id))).data,
    enabled: enabled() && !!id,
    refetchInterval: 15_000,
  });
}

export function useAgentReply(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { body: string; is_internal_note: boolean }) =>
      (await api.post(ApiPaths.supportAgentTicketReply(id), input)).data,
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ["support-agent-ticket", id] }),
  });
}

export function useAgentStatus(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (status: string) =>
      (await api.post(ApiPaths.supportAgentTicketStatus(id), { status })).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["support-agent-ticket", id] });
      void qc.invalidateQueries({ queryKey: ["support-agent-queue"] });
    },
  });
}

export function useAgentAssign(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await api.post(ApiPaths.supportAgentTicketAssign(id))).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["support-agent-ticket", id] });
      void qc.invalidateQueries({ queryKey: ["support-agent-queue"] });
      void qc.invalidateQueries({ queryKey: ["support-agent-stats"] });
    },
  });
}

/** Cross-org tickets for one organization — used by the platform console's
 * org-detail page. Reuses the agent queue with an `org` filter. */
export function useOrgSupportTickets(orgId: string | number | undefined) {
  return useQuery<Ticket[]>({
    queryKey: ["support-org-tickets", String(orgId)],
    queryFn: async () =>
      (
        await api.get<Paginated<Ticket>>(ApiPaths.supportAgentTickets, {
          params: { org: String(orgId), page_size: SUPPORT_PAGE_SIZE },
        })
      ).data.results ?? [],
    enabled: enabled() && !!orgId,
  });
}

export async function agentJoinCall(ticketId: string): Promise<CallPayload> {
  return (await api.post<CallPayload>(ApiPaths.supportAgentCallJoin(ticketId))).data;
}
